import React from 'react';

export const PdfUpload = ({
  label = "Upload Document (PDF)",
  file,
  onFileChange,
  onFileDelete,
  accept = "image/*,.pdf",
  placeholder = "Drop your file here or click to browse",
  disabled = false,
  viewOnly = false,
  loading = false,
  themed = false // light/dark styling for pages built on the jp-* tokens
}) => {
  // Classes that differ between the original dark look and the themed look
  const ui = themed
    ? {
        label: 'block text-sm font-semibold text-jp-ink mb-1.5 text-left',
        box: 'bg-jp-field border-jp-line',
        boxHover: 'hover:border-jp-accent hover:bg-jp-accent/5',
        text: 'text-jp-ink',
        subtext: 'text-jp-muted',
        empty: 'text-jp-muted',
        emptyIcon: 'border-jp-line',
      }
    : {
        label: 'block text-white text-sm font-medium mb-2 text-left',
        box: 'bg-slate-700/70 backdrop-blur-md border-slate-600/50',
        boxHover: 'hover:border-cyan-400 hover:bg-slate-600/70',
        text: 'text-white',
        subtext: 'text-slate-400',
        empty: 'text-slate-400',
        emptyIcon: 'border-slate-500',
      };

  const handleFileUpload = (e) => {
    if (disabled || loading) return;
    const selectedFile = e.target.files[0];
    if (selectedFile && onFileChange) {
      onFileChange(selectedFile);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onFileDelete && !loading) {
      onFileDelete();
    }
  };

  return (
    <div>
      {label && (
        <label className={ui.label}>
          {label}
        </label>
      )}
      <div className={`relative w-full min-h-[100px] px-5 py-8 rounded-xl ${ui.box} border-2 border-dashed transition-all ${
        (disabled || loading) && !viewOnly ? 'cursor-not-allowed opacity-60' : viewOnly ? '' : `cursor-pointer ${ui.boxHover}`
      }`}>
        {!viewOnly && (
          <input
            type="file"
            onChange={handleFileUpload}
            accept={accept}
            disabled={disabled}
            className={`absolute inset-0 w-full h-full opacity-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          />
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-4">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            <p className={`${ui.subtext} text-sm`}>Uploading...</p>
          </div>
        ) : file ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-white text-lg font-bold">
                  {(file.name || file.filename || file.documentUrl || '').toLowerCase().includes('.pdf') || file.documentUrl ? 'PDF' : '📷'}
                </span>
              </div>
              <div className="text-left">
                <p className={`${ui.text} text-sm font-medium`}>
                  {file.name || file.filename || file.documentUrl?.split('/').pop() || 'Uploaded Document'}
                </p>
                {file.size && (
                  <p className={`${ui.subtext} text-xs mt-1`}>
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                )}
                {file.uploadedAt && !file.size && (
                  <p className={`${ui.subtext} text-xs mt-1`}>
                    Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            {onFileDelete && !viewOnly && !disabled && (
              <button
                onClick={handleDelete}
                className="px-3 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center h-full">
            <div className={`flex items-center gap-4 ${ui.empty}`}>
              <div className={`w-16 h-16 border-2 border-dashed ${ui.emptyIcon} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-sm text-left">{placeholder}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfUpload;
